import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  rexue: '热血', lianai: '恋爱', qihuan: '奇幻', gaoxiao: '搞笑',
  zhanji: '战斗', xuanyi: '悬疑', richang: '日常', kehuan: '科幻',
  maoxian: '冒险', xiaoyuan: '校园', zixun: '动漫资讯', guoman: '国漫',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-medium text-foreground-950 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, 3);
  const categoryName = categoryNameMap[article.category] || '动漫资讯';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-50 border-b border-background-200">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">{categoryName}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 line-clamp-1">{article.title}</span>
              </nav>
              <h1 className="text-xl md:text-3xl font-heading text-foreground-950 leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </div>
                  阅读约{article.readTime}
                </span>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-8 rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.coverUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover object-top"
                title={`${article.title} - 樱花动漫之家`}
              />
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="prose max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-secondary-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
                <Link
                  to="/category/zixun"
                  className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer"
                >
                  返回列表 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="text-lg font-heading text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-secondary-200 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={rel.coverUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        title={`${rel.title} - 樱花动漫之家`}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2 text-xs text-foreground-500">
                        <span>{rel.date}</span>
                        <span>·</span>
                        <span>{rel.readTime}</span>
                      </div>
                      <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <script type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "description": article.excerpt,
              "author": { "@type": "Person", "name": article.author },
              "datePublished": article.date,
              "url": `https://www.yinghuadongman.com/article/${article.id}`,
              "publisher": {
                "@type": "Organization",
                "name": "樱花动漫之家",
                "url": "https://www.yinghuadongman.com/"
              }
            })
          }}
        />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.yinghuadongman.com/" },
                { "@type": "ListItem", "position": 2, "name": categoryName, "item": `https://www.yinghuadongman.com/category/${article.category}` },
                { "@type": "ListItem", "position": 3, "name": article.title }
              ]
            })
          }}
        />
      </main>
      <Footer />

      <style>{`
        .article-content h2 {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          color: oklch(var(--foreground-950));
        }
        .article-content p {
          margin-bottom: 1rem;
          line-height: 1.8;
          color: oklch(var(--foreground-800));
        }
      `}</style>
    </>
  );
}